import React from "react";
import { Modal2 } from "@iq/models";
import { getCompatibilityRules, createCompatibilityRules, deleteCompatibilityRule } from "../../services/productService";
import SelectionPanel from "./SelectionPanel";
import styles from "./addIncompatibility.module.css";
import { TbMobiledataOff } from "react-icons/tb";
import { LuPlus, LuTrash } from "react-icons/lu";

const emptySelection = () => ({ attribute_id: "", entire_group: true, value_ids: [] });
const ruleKey = (rule) => `${rule.legacy ? "legacy" : "rule"}-${rule.id}`;
const pairKey = (a, av, b, bv) => a < b ? `${a}:${av}/${b}:${bv}` : `${b}:${bv}/${a}:${av}`;

function coversPair(rule, a, av, b, bv) {
    const matches = (group, option, groupId, valueId) => group === groupId && (option === 0 || option === valueId);
    return (matches(rule.left_attribute_id, rule.left_value_id, a, av)
        && matches(rule.right_attribute_id, rule.right_value_id, b, bv))
        || (matches(rule.left_attribute_id, rule.left_value_id, b, bv)
            && matches(rule.right_attribute_id, rule.right_value_id, a, av));
}

export default function AddIncompatibility({ isOpen, productId, attributes = [], onClose, onChanged }) {
    const groups = React.useMemo(() => attributes.map((attribute) => ({
        id: Number(attribute.attribute_id ?? attribute.AttributeID),
        name: String(attribute.name ?? attribute.Name ?? ""),
        required: Boolean(attribute.required ?? attribute.Required),
        values: (attribute.values ?? attribute.Values ?? []).map((value) => ({
            id: Number(value.id ?? value.ID), value: String(value.value ?? value.Value ?? ""),
        })),
    })), [attributes]);
    const [left, setLeft] = React.useState(emptySelection);
    const [right, setRight] = React.useState(emptySelection);
    const [rules, setRules] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState("");
    const [notice, setNotice] = React.useState("");
    const [needsRefresh, setNeedsRefresh] = React.useState(false);
    const [removing, setRemoving] = React.useState(null);
    const [loadAttempt, setLoadAttempt] = React.useState(0);
    const hostRef = React.useRef(null);
    const busyRef = React.useRef(false);

    React.useEffect(() => {
        if (!isOpen) return undefined;
        const previousFocus = document.activeElement;
        setLeft(emptySelection());
        setRight(emptySelection());
        setRemoving(null);
        setNotice("");
        setNeedsRefresh(false);
        const frame = requestAnimationFrame(() => hostRef.current?.querySelector("select, button")?.focus());
        return () => {
            cancelAnimationFrame(frame);
            if (previousFocus?.isConnected) previousFocus.focus();
        };
    }, [isOpen, productId]);

    React.useEffect(() => {
        if (!isOpen) return undefined;
        let active = true;
        setLoading(true);
        setLoaded(false);
        setError("");
        getCompatibilityRules(productId).then((items) => {
            if (active) {
                setRules(items);
                setLoaded(true);
            }
        }).catch((loadError) => {
            if (active) setError(loadError.message || "Failed to load incompatibilities.");
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [isOpen, productId, loadAttempt]);

    const leftGroup = groups.find((group) => group.id === Number(left.attribute_id));
    const rightGroup = groups.find((group) => group.id === Number(right.attribute_id));
    const valid = leftGroup && rightGroup && leftGroup.id !== rightGroup.id
        && leftGroup.values.length > 0 && rightGroup.values.length > 0
        && (left.entire_group || left.value_ids.length > 0)
        && (right.entire_group || right.value_ids.length > 0);
    const candidates = valid ? (left.entire_group ? [0] : left.value_ids).flatMap((leftId) => (
        (right.entire_group ? [0] : right.value_ids).map((rightId) => ({
            left_attribute_id: leftGroup.id, left_value_id: leftId,
            right_attribute_id: rightGroup.id, right_value_id: rightId,
        }))
    )) : [];
    const existingKeys = new Set(rules.map((rule) => pairKey(rule.left_attribute_id, rule.left_value_id, rule.right_attribute_id, rule.right_value_id)));
    const duplicate = candidates.some((rule) => existingKeys.has(pairKey(rule.left_attribute_id, rule.left_value_id, rule.right_attribute_id, rule.right_value_id)));
    const requiredGroups = groups.filter((group) => group.required && group.values.length > 0);
    const findImpossible = (allRules) => {
        for (let i = 0; i < requiredGroups.length; i += 1) {
            for (let j = i + 1; j < requiredGroups.length; j += 1) {
                const a = requiredGroups[i];
                const b = requiredGroups[j];
                if (a.values.every((av) => b.values.every((bv) => allRules.some((rule) => coversPair(rule, a.id, av.id, b.id, bv.id))))) {
                    return `${a.name} and ${b.name} are both required, but no options from these groups can be combined. Customers will only be asked for whichever they reach first; the other will be skipped.`;
                }
            }
        }
        return "";
    };
    const existingWarning = loaded ? findImpossible(rules) : "";
    const proposedWarning = valid && loaded ? findImpossible([...rules, ...candidates]) : "";

    const describeSelection = (selection, group) => {
        if (!group) return "";
        return selection.entire_group ? `any option in ${group.name}`
            : `${group.values.filter((item) => selection.value_ids.includes(item.id)).map((item) => item.value).join(", ")} (${group.name})`;
    };
    const describeSide = (groupId, valueId) => {
        const group = groups.find((item) => item.id === groupId);
        if (!group) return `Unavailable group #${groupId}`;
        return valueId === 0 ? `Entire ${group.name} group`
            : `${group.values.find((item) => item.id === valueId)?.value ?? `Unavailable option #${valueId}`} (${group.name})`;
    };
    const requestClose = () => {
        if (busyRef.current) return;
        if ((left.attribute_id || right.attribute_id) && !window.confirm("Discard this unfinished incompatibility selection? Saved rules will remain.")) return;
        onClose();
    };
    const refreshPage = async () => {
        try {
            await onChanged();
            setNeedsRefresh(false);
        } catch (refreshError) {
            setNeedsRefresh(true);
            setError(`Rules were saved, but the service page could not refresh: ${refreshError.message || "Please retry."}`);
        }
    };
    const mutate = async (rule) => {
        if (busyRef.current || !loaded || (!rule && (!valid || duplicate))) return;
        if (!rule && proposedWarning && !window.confirm(`${proposedWarning}\n\nCreate these rules anyway?`)) return;
        busyRef.current = true;
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const updated = rule ? await deleteCompatibilityRule(productId, rule)
                : await createCompatibilityRules(productId, { left, right });
            setRules(updated);
            setRemoving(null);
            if (!rule) {
                setLeft(emptySelection());
                setRight(emptySelection());
            }
            setNotice(rule ? "Incompatibility removed." : "Incompatibility rules created.");
            await refreshPage();
        } catch (saveError) {
            setError(saveError.message || "Failed to save incompatibilities.");
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };
    const retryRefresh = async () => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setError("");
        try {
            await refreshPage();
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };
    const handleKeys = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            requestClose();
        }
        if (event.key !== "Tab") return;
        const items = Array.from(hostRef.current.querySelectorAll("button:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex='0']"))
            .filter((item) => !item.matches(":disabled") && item.getClientRects().length > 0);
        const first = items[0];
        const last = items[items.length - 1];
        if (!items.includes(document.activeElement)) {
            event.preventDefault();
            first?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
        }
    };
    const actionButtons = [
        { key: "save", text: "Save Changes", onClick: requestClose, disabled: busy,
            style: { background: "var(--surface-1)", color: "var(--fg-default)", borderColor: "var(--border-default)" } },
        { key: "create", text: candidates.length > 1 ? `Create ${candidates.length} rules` : "Create rule", onClick: () => mutate(), disabled: busy || !loaded || !valid || duplicate, icon: <LuPlus /> }
    ];

    if (!isOpen) return null;
    return (
        <div ref={hostRef} className={styles.host} aria-busy={busy || loading} onKeyDown={handleKeys}>
            <Modal2 isOpen={isOpen} onClose={requestClose} title="Manage incompatibilities"
                classes={{ content: styles.content }}
                styles={{
                    root: { padding: "var(--space-2)" },
                    panel: { width: "min(96vw, 1080px)", height: "min(94dvh, 840px)", minWidth: 0, minHeight: 0 },
                    header: { flexShrink: 0 },
                    footer: { flexShrink: 0 },
                }}
                actionButtons={actionButtons}>
                <div className={styles.body}>
                    {error && <div className={styles.error} role="alert">
                        <span>{error}</span>
                        {!loaded && !loading && <button type="button" className={styles.smallButton} onClick={() => setLoadAttempt((value) => value + 1)}>Retry loading</button>}
                        {needsRefresh && <button type="button" className={styles.smallButton} disabled={busy} onClick={retryRefresh}>Refresh service page</button>}
                    </div>}
                    {notice && <p className={styles.notice} role="status">{notice}</p>}
                    <div className={styles.layout}>
                        <section className={styles.rulesPanel} aria-labelledby="incompatibility-saved-heading">
                            <div className={styles.panelHeader}>
                                <h2 className={styles.sectionTitle} id="incompatibility-saved-heading">Existing rules</h2>
                                {loaded && <span className={styles.badge}>{rules.length}</span>}
                            </div>
                            {existingWarning && <p className={styles.warning} role="alert">{existingWarning}</p>}
                            {loading ? <p className={styles.muted} role="status">Loading incompatibilities...</p> : loaded && rules.length === 0 ? (
                                <div className={styles.empty}>No incompatibilities yet. All options can be combined across groups.</div>
                            ) : loaded && (
                                <ul className={styles.rules}>
                                    {rules.map((rule) => <li className={styles.rule} key={ruleKey(rule)}>
                                        <div className={styles.ruleBody}>
                                            <strong className={styles.ruleSide}>{describeSide(rule.left_attribute_id, rule.left_value_id)}</strong>
                                            <span className={styles.ruleJoin}>cannot combine with</span>
                                            <strong className={styles.ruleSide}>{describeSide(rule.right_attribute_id, rule.right_value_id)}</strong>
                                            <span className={styles.badge}>{rule.left_value_id === 0 || rule.right_value_id === 0 ? "Includes future options" : "Specific options"}</span>
                                        </div>
                                        {removing === ruleKey(rule) ? (
                                            <div className={styles.confirm}>
                                                <span className={styles.confirmText}>Remove this rule?</span>
                                                <button type="button" className={`${styles.smallButton} ${styles.smallButtonDanger}`} disabled={busy} onClick={() => mutate(rule)}>Remove</button>
                                                <button type="button" className={styles.smallButton} disabled={busy} onClick={() => setRemoving(null)}>Keep rule</button>
                                            </div>
                                        ) : <button type="button" className={styles.removeButton} disabled={busy} aria-label={`Remove rule: ${describeSide(rule.left_attribute_id, rule.left_value_id)} and ${describeSide(rule.right_attribute_id, rule.right_value_id)}`}
                                            onClick={() => setRemoving(ruleKey(rule))}><LuTrash /></button>}
                                    </li>)}
                                </ul>
                            )}
                        </section>
                        <section className={styles.builderPanel} aria-label="Create incompatibility rules">
                            {groups.length < 2 ? <p className={styles.hint}>Add at least two attribute groups to create an incompatibility.</p> : (
                                <>
                                    <h2 className={styles.sectionTitle}>Create incompatibility rule</h2>
                                    <div className={styles.selections}>
                                        <SelectionPanel label="Selection A" selection={left} otherId={right.attribute_id} groups={groups} disabled={busy || !loaded} onChange={setLeft} />
                                        <span className={styles.relationship} aria-hidden="true"><TbMobiledataOff /></span>
                                        <SelectionPanel label="Selection B" selection={right} otherId={left.attribute_id} groups={groups} disabled={busy || !loaded} onChange={setRight} />
                                    </div>
                                    {valid && <div className={styles.preview} aria-live="polite">
                                        <strong className={styles.previewLabel}>Rule preview</strong>
                                        <p className={styles.previewText}>{`${describeSelection(left, leftGroup)} cannot be combined with ${describeSelection(right, rightGroup)}.`}</p>
                                        {(left.entire_group || right.entire_group) && <span className={styles.badge}>Entire-group selections include future options</span>}
                                        {duplicate && <p className={styles.errorText}>One or more of these exact rules already exist. Adjust your selection.</p>}
                                        {proposedWarning && <p className={styles.warning} role="alert">{proposedWarning}</p>}
                                    </div>}
                                </>
                            )}
                        </section>

                    </div>
                </div>
            </Modal2>
        </div>
    );
}
